<template>
  <header>
    <div class="container container-nzidoo">
      <!-- Logo -->
      <router-link class="navbar-brand m-0" :to="{ name: 'index' }">
        <img class="logo" :src="logo" />
      </router-link>

      <!-- Search bar (Desktop only) -->
      <form v-if="!isDashboard" class="search-form" @submit.prevent="handleSearch">
        <div class="search-container">
          <div class="search-input-group">
            <input v-model="search" type="text" class="search-input" :placeholder="$t('sentence.searchByName')" />
            <i :class="isFilteredBySearch ? 'mdi-close' : 'mdi-magnify'"
              @click="isFilteredBySearch ? clearSearch() : handleSearch()" class="mdi search-nav-icon"
              aria-label="Search"></i>
          </div>
        </div>
      </form>

      <!-- Sell Button -->
      <router-link v-if="!isDashboard" :to="user && isLoggedIn
        ? { name: 'productSell' }
        : { name: 'SellingStepsExplanation' }
        " class="d-none d-md-block">
        <button class="btn btn-primary">
          <i class="mdi mdi-plus"></i> {{ $t('header.sellItems') }}
        </button>
      </router-link>

      <!-- Header Actions Component -->
      <ToggleActionButtons />

      <!-- Mobile toggle -->
      <button class="mobile-toggle" @click="mobileMenuOpen = !mobileMenuOpen" aria-label="Toggle navigation">
        <span></span><span></span><span></span>
      </button>
    </div>

    <!-- Search bar (Mobile only) -->
    <form v-if="!isDashboard" @submit.prevent="handleSearch" class="mobile-search-form d-block d-md-none">
      <div class="mobile-search-group">
        <div class="search-input-group">
          <input v-model="search" type="text" class="search-input mobile-search-input"
            :placeholder="$t('sentence.searchByName')" />
          <i @click="isFilteredBySearch ? clearSearch() : handleSearch()"
            :class="isFilteredBySearch ? 'mdi-close' : 'mdi-magnify'" class="mdi search-nav-icon"
            aria-label="Search"></i>
        </div>
      </div>
    </form>

    <!-- Mobile Menu -->
    <div class="mobile-menu" v-if="mobileMenuOpen">
      <div class="mobile-auth-buttons" v-if="!user || Object.keys(user).length === 0">
        <router-link :to="{ name: 'register' }" class="btn-mobile">{{ $t('header.signUp') }}</router-link>
        <router-link :to="{ name: 'login' }" class="btn-mobile active">{{ $t('header.signIn') }}</router-link>
      </div>

      <ul v-else class="nav navbar-nav">
        <li class="dropdown user-menu">
          <button class="nav-link d-flex align-items-center ml-2 gap-4" @click="moveToAccount">
            <img :src="user.picture ? storageUrl + user.picture : userpng" class="contact-avatar" alt="User Image" />
            <span class="user-name">{{ user.firstname }} {{ user.lastname }}</span>
          </button>
          <ul class="dropdown-menu-class">
            <li v-for="item in simpleMenuItems" :key="item.routeName">
              <router-link :to="{ name: item.routeName }" @click.native="mobileDropdownOpen = false">
                <i :class="item.icon"></i> {{ getTranslatedLabel(item) }}
              </router-link>
            </li>
            <li class="divider"></li>
            <li class="dropdown-footer">
              <a @click="logoutMobile">
                <i class="mdi mdi-logout"></i> {{ $t('auth.logout') }}
              </a>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </header>
</template>

<script>
import config from "@/config";
import logo from "@/assets/img/nzidoo-logo.png";
import userpng from '@/assets/img/user/user.webp';
import helper from '@/services/helper';
import ToggleActionButtons from './ToggleActionButtons.vue';
import {
  filterByPermissions,
  getMenuItems,
  getVisibleSubItems,
} from '../navigationConfig';

export default {
  name: "HeaderNav",
  components: {
    ToggleActionButtons
  },
  props: {
    searchQuery: {
      type: String,
      default: '',
    },
    isDashboard: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      userpng: userpng,
      search: this.searchQuery || (this.$route && this.$route.query && this.$route.query.search) || '',
      mobileMenuOpen: false,
      mobileDropdownOpen: false,
      storageUrl: config.storageUrl,
      logo,
    };
  },
  created() {
    this.$http.interceptors.response.use(
      undefined,
      (err) => {
        if (err && err.status === 401 && err.config && !err.config.__isRetryRequest) {
          this.$store.dispatch("logout");
        }
        return Promise.reject(err);
      }
    );
  },
  watch: {
    searchQuery(newVal) {
      if (newVal !== this.search) {
        this.search = newVal;
      }
    },
    '$route.query.search'(newVal) {
      if (newVal !== this.search) {
        this.search = newVal || '';
      }
    },
  },
  methods: {
    clearSearch() {
      this.search = '';
      if (this.$route.name === 'ProductsFilters') {
        const query = { ...this.$route.query };
        delete query.search;
        this.$router.push({
          name: 'ProductsFilters',
          query
        }).catch(() => { });
      }
      this.$emit('update:searchQuery', '');
    },
    handleSearch() {
      if (this.search && this.search.trim() !== '') {
        this.$emit('update:searchQuery', this.search);
        this.$router.push({
          name: 'ProductsFilters',
          query: { search: this.search }
        });
      }
    },
    logoutMobile() {
      this.mobileDropdownOpen = false;
      this.$store.dispatch("logout").then(() => {
        this.$router.push("/login");
      });
    },
    moveToAccount() {
      this.$router.push({ name: 'account' });
    },
    getTranslatedLabel: helper.getTranslatedLabel,
  },
  computed: {
    categories: {
      get() {
        return this.$store.state.categories;
      },
      set(value) {
        this.$store.commit("setCategories", { categories: value });
      },
    },
    isLoggedIn() {
      return this.$store.getters.isLoggedIn;
    },
    user: {
      get() {
        return this.$store.state.user;
      },
      set(user) {
        this.$store.commit("setUser", { user: user });
      },
    },
    visibleMenuItems() {
      const items = getMenuItems(this.user);
      return filterByPermissions(items, this.user);
    },
    flattenedMenuItems() {
      const flattened = [];
      this.visibleMenuItems.forEach(item => {
        if (item.hasSubmenu && item.subItems) {
          const visibleSubItems = getVisibleSubItems(item.subItems, this.user);
          flattened.push(...visibleSubItems);
        } else {
          flattened.push(item);
        }
      });
      return flattened;
    },
    simpleMenuItems() {
      return this.flattenedMenuItems;
    },
    submenuItems() {
      return this.visibleMenuItems.filter(item => item.hasSubmenu);
    },
    isFilteredBySearch() {
      return this.$route.name === 'ProductsFilters' &&
        this.$route.query &&
        this.$route.query.search &&
        this.$route.query.search.trim() !== '';
    }
  },
};
</script>

<style scoped>
select.form-control:not([size]):not([multiple]) {
  height: auto !important;
}

.container {
  width: 100%;
  margin-right: auto;
  margin-left: auto;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  max-width: 1450px;
}

.logo {
  width: 120px;
  height: 30px;
  margin-inline-start: 8px;
}

@media (min-width: 1536px) {
  .container-nzidoo {
    max-width: 1536px;
  }
}

.navbar-brand {
  font-weight: 700;
  font-size: 1.2rem;
  text-decoration: none;
  color: var(--color-0);
  white-space: nowrap;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.navbar-brand:hover {
  transform: scale(1.02);
}

.categories-dropdown {
  flex: 0 0 auto;
  width: 140px;
}

.categories-dropdown .form-control {
  border: none;
  border-right: 2px solid var(--light);
  border-radius: 0;
  margin: 0;
  padding: 12px 14px;
  height: 100%;
  background-color: transparent;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-3);
  cursor: pointer;
  outline: none;
}

.categories-dropdown .form-control:hover {
  background-color: var(--light);
}

.btn-block {
  width: 100%;
  justify-content: center;
}

.mobile-toggle {
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  flex-shrink: 0;
}

.mobile-toggle span {
  width: 24px;
  height: 3px;
  background-color: var(--color-3);
  border-radius: 2px;
  transition: all 0.3s ease;
}

/* ============================
   MOBILE MENU OPTIMIZATION
   ============================ */

.mobile-menu {
  display: none;
  background: linear-gradient(to bottom, white 0%, var(--light) 100%);
  padding: 0;
  border-top: 1px solid var(--light);
  animation: slideDown 0.3s ease;
  width: 100%;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  max-height: calc(100vh - 140px);
  overflow-y: auto;
}

/* Inner container for consistent padding */
.mobile-menu-inner {
  padding: 20px 16px;
}

/* Smooth slide down animation */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ============================
   MOBILE AUTH BUTTONS
   ============================ */

.mobile-auth-buttons {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  padding: 0 4px;
}

.btn-mobile {
  flex: 1;
  padding: 14px 20px;
  border-radius: 8px;
  text-decoration: none;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  border: 2px solid var(--light);
  background-color: white;
  color: var(--color-3);
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.btn-mobile:hover {
  border-color: var(--color-nz);
  color: var(--color-nz);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(33, 150, 243, 0.15);
}

.btn-mobile.active {
  background: linear-gradient(135deg, var(--color-nz) 0%, var(--color-nz-200) 100%);
  color: white;
  border-color: var(--color-nz);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.btn-mobile.active:hover {
  background: linear-gradient(135deg, var(--color-nz-200) 0%, var(--color-nz-300) 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(33, 150, 243, 0.4);
}

/* ============================
   USER MENU IN MOBILE
   ============================ */

.mobile-menu .nav {
  list-style: none;
  padding: 0;
  /* margin: 0 0 20px 0; */
  margin: 0;
}

.mobile-menu .user-menu {
  margin-bottom: 0;
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.mobile-menu .dropdown-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, var(--light) 0%, white 100%);
  border: none;
  cursor: pointer;
  padding: 16px;
  width: 100%;
  font-size: 15px;
  color: var(--color-3);
  font-weight: 600;
  border-bottom: 1px solid var(--light);
  transition: background 0.2s ease;
}

.mobile-menu .dropdown-toggle:active {
  background: var(--light);
}

.mobile-menu .user-name {
  flex: 1;
  text-align: left;
  font-size: 16px;
  color: #1a1a1a;
}

/* ============================
   DROPDOWN MENU IN MOBILE
   ============================ */

.mobile-menu .dropdown-menu-class {
  list-style: none;
  padding: 0;
  margin: 0;
  background-color: white;
  border: none;
  border-radius: 0;
  box-shadow: none;
  max-height: none;
  overflow-y: visible;
  min-width: auto;
  min-height: auto;
}

.mobile-menu .dropdown-menu-class li {
  padding: 0;
  border-bottom: 1px solid var(--light);
}

.mobile-menu .dropdown-menu-class li:last-child {
  border-bottom: none;
}

.mobile-menu .dropdown-menu-class li a {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  color: var(--color-6);
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 15px;
  font-weight: 500;
}

.mobile-menu .dropdown-menu-class li a i {
  font-size: 20px;
  width: 24px;
  color: var(--color-6);
  transition: color 0.2s ease;
}

.mobile-menu .dropdown-menu-class li a:active {
  background: linear-gradient(90deg, var(--primary-light) 0%, var(--light) 100%);
  color: var(--color-nz);
}

.mobile-menu .dropdown-menu-class li a:active i {
  color: var(--color-nz);
}

/* Divider styling */
.mobile-menu .divider {
  height: 1px;
  background: linear-gradient(90deg,
      transparent 0%,
      var(--light) 20%,
      var(--light) 80%,
      transparent 100%);
  margin: 8px 0;
}

/* Logout footer styling */
.mobile-menu .dropdown-footer a {
  color: var(--color-oo);
  font-weight: 600;
}

.mobile-menu .dropdown-footer a i {
  color: var(--color-oo);
}

.mobile-menu .dropdown-footer a:active {
  background: linear-gradient(90deg, var(--light) 0%, var(--light) 100%);
  color: var(--color-oo);
}

/* ============================
   SCROLLBAR STYLING
   ============================ */

.mobile-menu::-webkit-scrollbar {
  width: 6px;
}

.mobile-menu::-webkit-scrollbar-track {
  background: var(--light);
}

.mobile-menu::-webkit-scrollbar-thumb {
  background: var(--gray-300);
  border-radius: 3px;
}

.mobile-menu::-webkit-scrollbar-thumb:hover {
  background: var(--gray-400);
}

/* Touch feedback */
@media (hover: none) and (pointer: coarse) {
  .mobile-menu .dropdown-menu-class li a,
  .btn-mobile,
  .mobile-menu .btn-primary {
    -webkit-tap-highlight-color: rgba(33, 150, 243, 0.1);
  }
}

.nav {
  list-style: none;
  padding: 0;
  margin: 16px 0;
}

.user-menu {
  margin-bottom: 16px;
}

.divider {
  height: 1px;
  background-color: var(--color-d);
  margin: 4px 0;
}

.dropdown-footer {
  border-top: 1px solid var(--color-d);
}

/* ============================
   RESPONSIVE ADJUSTMENTS
   ============================ */

@media (min-width: 767px) {
  .logo {
    top: 20px;
    left: 20px;
  }

  .mobile-toggle {
    display: none;
  }

  .mobile-menu {
    display: none !important;
  }
}

@media (max-width: 767px) {
  .logo {
    width: 100px;
    height: 30px;
  }

  .container,
  .mobile-search-form {
    width: 100%;
    padding: 0px 16px;
  }

  .mobile-search-form {
    margin-bottom: 6px;
  }

  .container {
    gap: 12px;
    padding: 10px 10px;
    align-items: flex-end;
  }

  .mobile-menu {
    display: block;
    z-index: 10000 !important;
  }
}

@media (max-width: 375px) {
  .mobile-menu-inner {
    padding: 16px 12px;
  }

  .mobile-auth-buttons {
    gap: 8px;
  }

  .btn-mobile {
    padding: 12px 16px;
    font-size: 14px;
  }

  .mobile-menu .user-name {
    font-size: 15px;
  }

  .mobile-menu .dropdown-menu-class li a {
    padding: 12px 14px;
    font-size: 14px;
    gap: 12px;
  }

  .mobile-menu .btn-primary {
    padding: 14px 20px;
    font-size: 15px;
  }
}

@media (max-width: 375px) {
  .container {
    gap: 10px;
    padding: 10px 10px;
  }
}
</style>